import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, timeout, retry, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DebtPoint {
  date: string;
  value: number;
}

@Injectable({ providedIn: 'root' })
export class DebtService {
  private readonly worldBankUrl = 'https://api.worldbank.org/v2/country/ARG/indicator/DT.DOD.DECT.CD?format=json&per_page=100';
  private lastUpdateTs: number | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene la serie histórica desde INDEC (trimestral, M USD)
   * Formato: { date: 'YYYY-QN', value: number }
   */
  getHistoricalDebt(): Observable<DebtPoint[]> {
    // Caché 6h en localStorage para INDEC, con invalidación si detectamos trimestre nuevo
    const cacheKey = 'indec_debt_cache_v1';
    const now = Date.now();
    const cachedRaw = typeof window !== 'undefined' ? localStorage.getItem(cacheKey) : null;
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        const ttlMs = 6 * 60 * 60 * 1000; // 6 horas
        const points: DebtPoint[] = Array.isArray(cached?.points) ? cached.points : [];
        const lastCached = points.length ? points[points.length - 1]?.date : null;
        // Calcular trimestre actual
        const today = new Date();
        const y = today.getFullYear();
        const m = today.getMonth() + 1; // 1-12
        const q = Math.ceil(m / 3);
        const currentQuarter = `${y}-Q${q}`;
        const toKey = (d: string | null) => {
          if (!d) return -1;
          const m = /^(\d{4})-Q(\d)$/.exec(d);
          if (m) return Number(m[1]) * 10 + Number(m[2]);
          const yy = Number(String(d).slice(0, 4));
          return isNaN(yy) ? -1 : yy * 10;
        };
        const isFresh = !!(cached && cached.ts && (now - cached.ts) < ttlMs);
        const hasLatestQuarter = toKey(lastCached) >= toKey(currentQuarter);
        // Solo devolvemos caché si está fresco y contiene al menos el trimestre actual
        if (isFresh && hasLatestQuarter) {
          this.lastUpdateTs = cached.ts;
          return of(points);
        }
      } catch (_) {}
    }

    return this.getIndecQuarterlySeries().pipe(
      map(points => {
        // Guardar en caché solo si hay datos
        try {
          if (typeof window !== 'undefined' && Array.isArray(points) && points.length) {
            localStorage.setItem(cacheKey, JSON.stringify({ ts: now, points }));
          }
        } catch (_) {}
        this.lastUpdateTs = now;
        return points;
      })
    );
  }

  /** Devuelve el último valor disponible (no nulo) */
  getLatestDebt(): Observable<DebtPoint | null> {
    return this.getHistoricalDebt().pipe(
      map(points => {
        if (!points.length) return null;
        return points[points.length - 1];
      })
    );
  }

  /**
   * Fallback opcional a FRED: devuelve último valor % del PIB.
   * Requiere definir environment.fredApiKey y environment.fredSeriesId
   */
  getFredLatestDebtPercent(): Observable<number | null> {
    const apiKey = environment.fredApiKey;
    const seriesId = environment.fredSeriesId;
    if (!apiKey || !seriesId) return of(null);

    const url = 'https://api.stlouisfed.org/fred/series/observations';
    const params: any = {
      series_id: seriesId,
      api_key: apiKey,
      file_type: 'json'
    };

    return this.http.get<any>(url, { params }).pipe(
      map(resp => {
        const obs = Array.isArray(resp?.observations) ? resp.observations : [];
        const valid = obs.filter((o: any) => o?.value !== null && o.value !== '.');
        if (!valid.length) return null;
        const last = valid[valid.length - 1];
        const val = Number(last.value);
        return isNaN(val) ? null : val;
      })
    );
  }

  /** Devuelve la última observación de FRED con fecha y valor (% PIB) */
  getFredLatestObservation(): Observable<{ date: string; value: number } | null> {
    const apiKey = environment.fredApiKey;
    const seriesId = environment.fredSeriesId;
    if (!apiKey || !seriesId) return of(null);

    const url = 'https://api.stlouisfed.org/fred/series/observations';
    const params: any = {
      series_id: seriesId,
      api_key: apiKey,
      file_type: 'json'
    };

    return this.http.get<any>(url, { params }).pipe(
      map(resp => {
        const obs = Array.isArray(resp?.observations) ? resp.observations : [];
        const valid = obs.filter((o: any) => o?.value !== null && o.value !== '.');
        if (!valid.length) return null;
        const last = valid[valid.length - 1];
        const val = Number(last.value);
        if (isNaN(val)) return null;
        // FRED entrega fecha como YYYY-MM-DD; formateamos a trimestre si aplica
        const dateStr: string = typeof last?.date === 'string' ? last.date : '';
        let formatted = dateStr;
        if (dateStr && dateStr.length >= 7) {
          const year = dateStr.slice(0, 4);
          const month = dateStr.slice(5, 7);
          const mNum = Number(month);
          if (!isNaN(mNum) && mNum >= 1 && mNum <= 12) {
            const q = Math.ceil(mNum / 3);
            formatted = `${year}-Q${q}`;
          } else {
            formatted = year;
          }
        } else if (dateStr && dateStr.length >= 4) {
          formatted = dateStr.slice(0, 4);
        }
        return { date: formatted, value: val };
      })
    );
  }

  /** Devuelve timestamp (ms) de la última actualización/caché */
  getLastUpdateTimestamp(): number | null {
    return this.lastUpdateTs;
  }

  /** Devuelve la última observación de INDEC (series de tiempo) con fecha y valor */
  getIndecLatestObservation(): Observable<{ date: string; value: number } | null> {
    const seriesId = (environment as any).indecSeriesId;
    const csvUrl = (environment as any).indecCsvUrl;

    // Helper para formatear fecha YYYY-MM-DD a YYYY-QN
    const formatQuarter = (dateStr: string): string => {
      if (dateStr && dateStr.length >= 7) {
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(5, 7);
        const mNum = Number(month);
        if (!isNaN(mNum) && mNum >= 1 && mNum <= 12) {
          const q = Math.ceil(mNum / 3);
          return `${year}-Q${q}`;
        }
        return year;
      }
      if (dateStr && dateStr.length >= 4) {
        return dateStr.slice(0, 4);
      }
      return dateStr;
    };

    // Si hay seriesId, intentar API de series primero; si no hay datos, caer al CSV
    if (seriesId) {
      const url = 'https://apis.datos.gob.ar/series/api/series';
      const params: any = { ids: seriesId, format: 'json', collapse: 'quarter', collapse_aggregation: 'end_of_period', last: 1000 };
      return this.http.get<any>(url, { params }).pipe(
        timeout(10000),
        retry(2),
        map(resp => {
          const series = Array.isArray(resp?.series) ? resp.series[0] : null;
          if (!series) return null;
          const data = series.data;
          const index = series.index;

          // Caso index[] + data[]
          if (Array.isArray(index) && Array.isArray(data) && data.length === index.length) {
            for (let i = data.length - 1; i >= 0; i--) {
              const val = data[i];
              const lastDate = typeof index[i] === 'string' ? index[i] : String(index[i]);
              if (val !== null && val !== undefined && lastDate) {
                const lastVal = Number(val);
                if (!isNaN(lastVal)) return { date: formatQuarter(lastDate), value: lastVal };
              }
            }
            return null;
          }

          // Caso data como filas [fecha, valor]
          if (Array.isArray(data) && data.length) {
            for (let i = data.length - 1; i >= 0; i--) {
              const row = data[i];
              if (Array.isArray(row) && row.length >= 2) {
                const dateAny = row[0];
                const valAny = row[1];
                const dateStr = typeof dateAny === 'string' ? dateAny : String(dateAny);
                const num = Number(valAny);
                if (dateStr && !isNaN(num)) {
                  return { date: formatQuarter(dateStr), value: num };
                }
              }
            }
          }

          return null;
        }),
        // Fallback al CSV si la API no devuelve dato
        // Usamos switchMap para emitir otro observable cuando el resultado es null
        // Mantener compatibilidad con el tipo de retorno
        // Nota: si csvUrl no está definido, simplemente devolvemos null
        // eslint-disable-next-line rxjs/no-implicit-any-catch
        // @ts-ignore
        switchMap((res: { date: string; value: number } | null) => {
          if (res) return of(res);
          if (!csvUrl) return of(null);
          return this.http.get(csvUrl, { responseType: 'text' as 'json' }).pipe(
            timeout(10000),
            retry(2),
            map((text: any) => {
              const csv = String(text);
              const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0);
              if (!lines.length) return null;
              const header = lines[0].split(',');
              const timeIdx = header.indexOf('indice_tiempo');
              const totalIdx = header.indexOf('total_deuda_externa');
              if (timeIdx === -1 || totalIdx === -1) return null;
              for (let i = lines.length - 1; i >= 1; i--) {
                const cols = lines[i].split(',');
                const valStr = cols[totalIdx];
                const dateStr = cols[timeIdx];
                if (valStr !== undefined && valStr !== '' && dateStr) {
                  const lastVal = Number(valStr);
                  if (!isNaN(lastVal)) {
                    return { date: formatQuarter(dateStr), value: lastVal };
                  }
                }
              }
              return null;
            })
          );
        })
      );
    }

    // Fallback: CSV oficial del dataset si no hay seriesId
    if (csvUrl) {
      return this.http.get(csvUrl, { responseType: 'text' as 'json' }).pipe(
        map((text: any) => {
          const csv = String(text);
          const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0);
          if (!lines.length) return null;
          const header = lines[0].split(',');
          const timeIdx = header.indexOf('indice_tiempo');
          const totalIdx = header.indexOf('total_deuda_externa');
          if (timeIdx === -1 || totalIdx === -1) return null;
          for (let i = lines.length - 1; i >= 1; i--) {
            const cols = lines[i].split(',');
            const valStr = cols[totalIdx];
            const dateStr = cols[timeIdx];
            if (valStr !== undefined && valStr !== '' && dateStr) {
              const lastVal = Number(valStr);
              if (!isNaN(lastVal)) {
                return { date: formatQuarter(dateStr), value: lastVal };
              }
            }
          }
          return null;
        })
      );
    }

    return of(null);
  }

  /** Obtiene serie trimestral completa desde INDEC (API de series si disponible; fallback CSV) en M USD */
  getIndecQuarterlySeries(): Observable<DebtPoint[]> {
    const seriesId = (environment as any).indecSeriesId;
    const csvUrl = (environment as any).indecCsvUrl;

    // Helper para formatear fecha YYYY-MM-DD a YYYY-QN
    const formatQuarter = (dateStr: string): string => {
      if (dateStr && dateStr.length >= 7) {
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(5, 7);
        const mNum = Number(month);
        if (!isNaN(mNum) && mNum >= 1 && mNum <= 12) {
          const q = Math.ceil(mNum / 3);
          return `${year}-Q${q}`;
        }
        return year;
      }
      if (dateStr && dateStr.length >= 4) {
        return dateStr.slice(0, 4);
      }
      return dateStr;
    };

    const sortPoints = (points: DebtPoint[]): DebtPoint[] => {
      const toKey = (d: string) => {
        const m = /^(\d{4})-Q(\d)$/.exec(d);
        if (m) return Number(m[1]) * 10 + Number(m[2]);
        const y = Number(d.slice(0, 4));
        return isNaN(y) ? 0 : y * 10;
      };
      return points.sort((a, b) => toKey(a.date) - toKey(b.date));
    };

    if (seriesId) {
      const url = 'https://apis.datos.gob.ar/series/api/series';
      const params: any = { ids: seriesId, format: 'json', collapse: 'quarter', collapse_aggregation: 'end_of_period', last: 1000 };
      return this.http.get<any>(url, { params }).pipe(
        timeout(10000),
        retry(2),
        map(resp => {
          const series = Array.isArray(resp?.series) ? resp.series[0] : null;
          if (!series) return [];
          const points: DebtPoint[] = [];
          const data = series.data;
          const index = series.index;

          // Caso 1: index[] + data[] paralelos
          if (Array.isArray(index) && Array.isArray(data) && data.length === index.length) {
            for (let i = 0; i < data.length; i++) {
              const val = data[i];
              const dateStr = typeof index[i] === 'string' ? index[i] : String(index[i]);
              if (val !== null && val !== undefined && dateStr) {
                const num = Number(val);
                if (!isNaN(num)) points.push({ date: formatQuarter(dateStr), value: num });
              }
            }
            return sortPoints(points);
          }

          // Caso 2: data como filas [fecha, valor]
          if (Array.isArray(data) && data.length) {
            for (let i = 0; i < data.length; i++) {
              const row = data[i];
              if (Array.isArray(row) && row.length >= 2) {
                const dateAny = row[0];
                const valAny = row[1];
                const dateStr = typeof dateAny === 'string' ? dateAny : String(dateAny);
                const num = Number(valAny);
                if (dateStr && !isNaN(num)) {
                  points.push({ date: formatQuarter(dateStr), value: num });
                }
              }
            }
            return sortPoints(points);
          }

          return [];
        }),
        // Si la API no devuelve puntos, intentamos el CSV como fallback
        // @ts-ignore
        switchMap((points: DebtPoint[]) => {
          if (Array.isArray(points) && points.length) return of(points);
          if (!csvUrl) return of([]);
          return this.http.get(csvUrl, { responseType: 'text' as 'json' }).pipe(
            timeout(10000),
            retry(2),
            map((text: any) => {
              const csv = String(text);
              const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0);
              if (lines.length < 2) return [];
              const header = lines[0].split(',');
              const timeIdx = header.indexOf('indice_tiempo');
              const totalIdx = header.indexOf('total_deuda_externa');
              if (timeIdx === -1 || totalIdx === -1) return [];
              const pts: DebtPoint[] = [];
              for (let i = 1; i < lines.length; i++) {
                const cols = lines[i].split(',');
                const valStr = cols[totalIdx];
                const dateStr = cols[timeIdx];
                if (valStr !== undefined && valStr !== '' && dateStr) {
                  const val = Number(valStr);
                  if (!isNaN(val)) {
                    pts.push({ date: formatQuarter(dateStr), value: val });
                  }
                }
              }
              return sortPoints(pts);
            })
          );
        })
      );
    }

    // Fallback CSV
    if (csvUrl) {
      return this.http.get(csvUrl, { responseType: 'text' as 'json' }).pipe(
        timeout(10000),
        retry(2),
        map((text: any) => {
          const csv = String(text);
          const lines = csv.split(/\r?\n/).filter(l => l.trim().length > 0);
          if (lines.length < 2) return [];
          const header = lines[0].split(',');
          const timeIdx = header.indexOf('indice_tiempo');
          const totalIdx = header.indexOf('total_deuda_externa');
          if (timeIdx === -1 || totalIdx === -1) return [];
          const points: DebtPoint[] = [];
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',');
            const valStr = cols[totalIdx];
            const dateStr = cols[timeIdx];
            if (valStr !== undefined && valStr !== '' && dateStr) {
              const val = Number(valStr);
              if (!isNaN(val)) {
                points.push({ date: formatQuarter(dateStr), value: val }); // M USD
              }
            }
          }
          return sortPoints(points);
        })
      );
    }

    return of([]);
  }
}